import { format } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { Button, Chip, CircularProgress, IconButton, Menu, MenuItem, SvgIcon } from '@mui/material';

import API from 'app/API';
import useLoginDialog from 'hooks/use-login-dialog';
import useStores from 'hooks/use-stores';
import { useConfigs } from 'main/modules/configs';
import { LockIcon } from 'pages/item/v2/components/ProtectedData';
import useHistoryOfChangesContext from 'pages/item/v2/hooks/use-history-of-changes-context';
import { HistoryOfChangesItem } from 'pages/item/v2/types/history-of-changes';
import isEmpty from 'utils/is-empty';

import DownloadIcon from './assets/download.svg';
import MilestoneIcon from './assets/milestone.svg';
import useStyles from './styles';

interface Props {
    id: string;
    published?: Date;
    className?: string;
}

const RevisionSelect: React.FC<Props> = ({ id, published, className }) => {
    const { classes, cx } = useStyles();
    const { userStore } = useStores();
    const { settings, environment } = useConfigs();
    const location = useLocation();
    const [dialog, openDialog] = useLoginDialog(location.pathname + location.search);

    const { data, revision, setVersion, isLoading, load } = useHistoryOfChangesContext();
    const date = revision?.published ?? published ?? null;

    const groups = useMemo(() => {
        const groups = new Map<string, HistoryOfChangesItem[]>();
        data.forEach((item) => {
            const key = format(item.published, 'LLL dd, yyyy');
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)?.push(item);
        });

        return [...groups.entries()];
    }, [data]);

    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const handleOpen = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            load();
            setAnchor(event.currentTarget);
        },
        [load]
    );
    const handleClose = useCallback(() => setAnchor(null), []);

    if (!userStore.isAuthorized && settings.limitDataEnabled) {
        return (
            <>
                <Chip
                    className={cx(classes.chip, classes.disabled, className)}
                    icon={<LockIcon className={classes.icon} />}
                    variant="outlined"
                    clickable
                    onClick={openDialog}
                    label={
                        <div className={classes.label}>
                            <span className={classes.text}>{'History'}</span>
                        </div>
                    }
                />
                {dialog}
            </>
        );
    }

    return (
        <>
            <Chip
                className={cx(classes.chip, className)}
                icon={<HistoryIcon className={classes.icon} />}
                variant="outlined"
                clickable
                onClick={handleOpen}
                label={
                    <div className={classes.label}>
                        <span className={classes.text}>{'History'}</span>
                        {date ? <span className={classes.date}>{format(date, 'LLL dd, yyyy - h:mm aaaa')}</span> : null}
                    </div>
                }
            />
            <Menu
                classes={{ paper: classes.menu, list: classes.menuList }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                anchorEl={anchor}
                open={!!anchor}
                onClose={handleClose}
            >
                <div className={classes.menuTitle}>
                    <span>{'Version history'}</span>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </div>
                {isLoading ? (
                    <div className={classes.spinner}>
                        <CircularProgress size={24} />
                    </div>
                ) : isEmpty(groups) ? (
                    <MenuItem disabled className={classes.empty}>
                        {'Nothing found'}
                    </MenuItem>
                ) : (
                    groups
                        .map(([name, items], index) => [
                            <div key={name} className={cx(classes.milestone, { [classes.first]: index === 0 })}>
                                <MilestoneIcon />
                                <span>{name}</span>
                            </div>,
                            ...items.map((item) => {
                                const changedFields = Object.keys(item.changes).join(', ');
                                return (
                                    <MenuItem
                                        key={item.version}
                                        onClick={() => {
                                            setVersion(item.version);
                                            handleClose();
                                        }}
                                        className={classes.menuItem}
                                        disableRipple
                                    >
                                        <div
                                            className={cx(classes.version, {
                                                [classes.selected]: item.version === revision?.version
                                            })}
                                        >
                                            <span>{`Version ${item.version}`}</span>
                                            {item.version === data[0].version ? (
                                                <Chip
                                                    label={'Current'}
                                                    classes={{ root: classes.current, label: classes.label }}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            ) : null}
                                        </div>
                                        <div className={classes.description}>
                                            <span>{'Updated fields: '}</span>
                                            <span className={classes.text} title={changedFields}>
                                                {changedFields}
                                            </span>
                                            <span className={classes.date}>{format(item.published, 'h:mm aaaa')}</span>
                                        </div>
                                    </MenuItem>
                                );
                            })
                        ])
                        .concat(
                            environment !== 'stable'
                                ? [
                                      <div key={'download-history'} className={classes.download}>
                                          <Button
                                              className={classes.button}
                                              variant="contained"
                                              href={API.SEARCH_HISTORY.url.replace('{id}', id)}
                                              target="__blank"
                                              startIcon={
                                                  <SvgIcon inheritViewBox>
                                                      <DownloadIcon />
                                                  </SvgIcon>
                                              }
                                          >
                                              {'Download history .json'}
                                          </Button>
                                      </div>
                                  ]
                                : []
                        )
                )}
            </Menu>
        </>
    );
};

export default RevisionSelect;
